function norm(s) {
    return (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}
function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Matches `needle` in `haystack` without treating substrings of words as hits
 * (e.g. "DEP" must not match inside "Department").
 * Long phrases still use substring match so "Department of Commerce" works on full titles.
 */
function matchesDepartmentTitlePhrase(haystack, needle) {
    const h = haystack.trim().toLowerCase();
    const n = needle.trim().toLowerCase();
    if (!n || !h)
        return false;
    if (h === n)
        return true;
    if (n.length >= 6)
        return h.includes(n);
    return new RegExp(`(^|[^a-z0-9])${escapeRegExp(n)}([^a-z0-9]|$)`, 'i').test(h);
}
/**
 * Resolves bundled `departments` hero config for the permit page URL segment (`:departmentId`)
 * and optional API hero row. Same idea as landing `matchTitles`: grid URLs like `/permits/1` can map
 * to the Commerce block via `matchTitles: ["1"]` without duplicating a separate `"1"` key.
 *
 * Order: (1) any block whose `matchTitles` equals the URL id → (2) exact JSON key →
 * (3) case-insensitive key → (4) API `department.title` vs `title` / `matchTitles`.
 */
export function resolvePermitPageStaticDepartment(departments, departmentId, apiDepartment) {
    if (!departmentId || !departments || Object.keys(departments).length === 0)
        return undefined;
    const idNorm = norm(departmentId);
    for (const def of Object.values(departments)) {
        const mt = def.matchTitles;
        if (mt?.length && mt.some((m) => norm(m) === idNorm))
            return def;
    }
    const direct = departments[departmentId];
    if (direct)
        return direct;
    const ciKey = Object.keys(departments).find((k) => norm(k) === idNorm);
    if (ciKey)
        return departments[ciKey];
    const apiTitle = apiDepartment?.title;
    const at = norm(apiTitle);
    if (at) {
        for (const def of Object.values(departments)) {
            if (def.title && norm(def.title) === at)
                return def;
        }
        for (const def of Object.values(departments)) {
            if (!def.matchTitles?.length)
                continue;
            const sorted = [...def.matchTitles].sort((a, b) => norm(b).length - norm(a).length);
            for (const m of sorted) {
                const mn = norm(m);
                if (!mn || !at)
                    continue;
                if (at === mn)
                    return def;
                if (matchesDepartmentTitlePhrase(at, mn))
                    return def;
                if (mn.length >= 3 && mn.includes(at) && at.length >= 3)
                    return def;
            }
        }
        for (const def of Object.values(departments)) {
            const t = def.title ? norm(def.title) : '';
            if (t.length < 4)
                continue;
            if (matchesDepartmentTitlePhrase(at, t))
                return def;
            if (t.length >= 6 && at.includes(t))
                return def;
            if (at.length >= 6 && t.includes(at))
                return def;
        }
    }
    return undefined;
}
/**
 * Resolves the URL's `:departmentId` segment (which may be a slug like `"commerce"`) to the
 * numeric department id the Flow Builder grids actually store (`DEPARTMENT: "1"`, etc.), using
 * the same `matchTitles` blocks as {@link resolvePermitPageStaticDepartment} — the first purely
 * numeric entry in the matching block's `matchTitles` is the grid's real id. Falls back to
 * `departmentId` unchanged when it's already numeric or no block matches (grid then just returns
 * no rows rather than throwing).
 */
export function resolvePermitPageDepartmentId(departments, departmentId) {
    if (/^\d+$/.test(departmentId.trim()))
        return departmentId.trim();
    if (!departments)
        return departmentId;
    const idNorm = norm(departmentId);
    for (const def of Object.values(departments)) {
        const mt = def.matchTitles;
        if (!mt?.length)
            continue;
        if (!mt.some((m) => norm(m) === idNorm))
            continue;
        const numeric = mt.find((m) => /^\d+$/.test(m.trim()));
        if (numeric)
            return numeric.trim();
    }
    return departmentId;
}
